import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MousePointerClick, Zap } from 'lucide-react';
import { staffBenefits } from '../data/mock';

const iconMap = {
  Calendar: Calendar,
  MousePointerClick: MousePointerClick,
  Zap: Zap,
};

const ForStaffSection = () => {
  return (
    <section id="for-staff" className="section-padding bg-white">
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Visual Card - Left side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative order-2 lg:order-1"
          >
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-black/5">
              {/* Mock Staff App */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-primary)' }}>
                    <span className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>TM</span>
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>Thabo M.</p>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg key={star} className="w-3 h-3 fill-yellow-400" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                      <span className="text-xs ml-1" style={{ color: 'var(--text-muted)' }}>4.9</span>
                    </div>
                  </div>
                </div>

                <h4 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                  Available Shifts Near You
                </h4>

                {/* Shift Listings */}
                {[
                  { venue: 'The Grand Hotel', role: 'Server', rate: 'R180/hr', time: 'Tonight 6PM', distance: '2.3km' },
                  { venue: 'Bistro 42', role: 'Bartender', rate: 'R200/hr', time: 'Tomorrow 5PM', distance: '4.1km' },
                  { venue: 'Sunset Events', role: 'Catering', rate: 'R150/hr', time: 'Sat 12PM', distance: '5.8km' },
                ].map((shift, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-xl mb-2 transition-all hover:shadow-md cursor-pointer"
                    style={{ background: i === 0 ? 'var(--accent-wash)' : 'var(--bg-section)' }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                          {shift.venue}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          {shift.role} • {shift.distance}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-sm" style={{ color: 'var(--accent-text)' }}>
                          {shift.rate}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          {shift.time}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Earnings */}
              <div className="pt-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>This Month's Earnings</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>R12,450</p>
                  </div>
                  <button className="btn-primary text-sm py-2 px-4">
                    View All Shifts
                  </button>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div
              className="absolute -z-10 -bottom-4 -left-4 w-full h-full rounded-3xl"
              style={{ background: 'var(--accent-wash)' }}
            />
          </motion.div>

          {/* Content - Right side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-2"
          >
            <span
              className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4"
              style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}
            >
              For Hospitality Staff
            </span>
            <h2 className="heading-2 mb-6">Work on your terms</h2>
            <p className="body-large mb-8">
              Take control of your career. Pick up shifts that fit your schedule,
              set your rates, and get paid fast for the work you do.
            </p>

            <div className="space-y-6">
              {staffBenefits.map((benefit, index) => {
                const Icon = iconMap[benefit.icon];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex gap-4"
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center"
                      style={{ background: 'var(--accent-wash)' }}
                    >
                      <Icon size={24} style={{ color: 'var(--accent-text)' }} />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                        {benefit.title}
                      </h4>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {benefit.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ForStaffSection;
